import { useEffect, useRef, useState } from "react";

type Phase = "hidden" | "walking-in" | "waving" | "walking-out";
type Side = "left" | "right";

const WALK_IN_MS = 3800;
const WAVE_MS = 2400;
const WALK_OUT_MS = 3800;
const FIRST_MIN_MS = 8_000;
const FIRST_MAX_MS = 20_000;
const IDLE_MIN_MS = 50_000;
const IDLE_MAX_MS = 140_000;
const INWARD_PX = 160;
const OFF_SCREEN_PX = 80;

const randBetween = (min: number, max: number) => min + Math.random() * (max - min);

type Step = "off" | "walk-in" | "on" | "walk-out";

export function StickFigure() {
  const [phase, setPhase] = useState<Phase>("hidden");
  const [side, setSide] = useState<Side>("left");
  const [step, setStep] = useState<Step>("off");
  const firstRef = useRef(true);

  useEffect(() => {
    if (phase === "hidden") {
      const delay = firstRef.current
        ? randBetween(FIRST_MIN_MS, FIRST_MAX_MS)
        : randBetween(IDLE_MIN_MS, IDLE_MAX_MS);
      firstRef.current = false;
      const t = setTimeout(() => {
        setSide(Math.random() < 0.5 ? "left" : "right");
        setPhase("walking-in");
      }, delay);
      return () => clearTimeout(t);
    }

    const next: Record<Exclude<Phase, "hidden">, { phase: Phase; delay: number }> = {
      "walking-in": { phase: "waving", delay: WALK_IN_MS },
      waving: { phase: "walking-out", delay: WAVE_MS },
      "walking-out": { phase: "hidden", delay: WALK_OUT_MS },
    };
    const t = setTimeout(() => setPhase(next[phase].phase), next[phase].delay);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (phase === "hidden") {
      setStep("off");
      return;
    }
    if (phase === "waving") {
      setStep("on");
      return;
    }
    if (phase === "walking-out") {
      setStep("walk-out");
      return;
    }
    // walking-in: settle at off-screen, then next frame animate on-screen
    setStep("off");
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setStep("walk-in"));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [phase, side]);

  const isLeft = side === "left";
  const direction = isLeft ? 1 : -1;
  const onScreen = step === "walk-in" || step === "on";
  const tx = onScreen ? INWARD_PX : -OFF_SCREEN_PX;
  const transitionMs =
    step === "walk-in" ? WALK_IN_MS :
    step === "walk-out" ? WALK_OUT_MS : 0;
  const isWalking = phase === "walking-in" || phase === "walking-out";
  const isWaving = phase === "waving";

  return (
    <>
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          .sf-root { display: none !important; }
        }
        .sf-leg-l, .sf-leg-r, .sf-arm-r {
          transform-box: view-box;
        }
        .sf-leg-l, .sf-leg-r { transform-origin: 0px 6px; }
        .sf-arm-r { transform-origin: 0px -13px; }

        .sf-walk .sf-leg-l { animation: sf-walk-l 0.55s infinite linear; }
        .sf-walk .sf-leg-r { animation: sf-walk-r 0.55s infinite linear; }
        @keyframes sf-walk-l {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-28deg); }
          75% { transform: rotate(28deg); }
        }
        @keyframes sf-walk-r {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(28deg); }
          75% { transform: rotate(-28deg); }
        }

        .sf-wave .sf-arm-r { animation: sf-wave 2.4s linear forwards; }
        @keyframes sf-wave {
          0% { transform: rotate(0deg); }
          15% { transform: rotate(-125deg); }
          28% { transform: rotate(-145deg); }
          42% { transform: rotate(-115deg); }
          56% { transform: rotate(-145deg); }
          70% { transform: rotate(-115deg); }
          85% { transform: rotate(-130deg); }
          100% { transform: rotate(0deg); }
        }
      `}</style>
      <div
        className="sf-root fixed bottom-4 pointer-events-none z-40 select-none"
        style={{
          [isLeft ? "left" : "right"]: 0,
          transform: `translateX(${direction * tx}px)`,
          transition: transitionMs ? `transform ${transitionMs}ms linear` : "none",
        }}
        aria-hidden="true"
      >
        <div style={{ transform: isLeft ? undefined : "scaleX(-1)" }}>
          <svg
            width="40"
            height="80"
            viewBox="-12 -28 24 48"
            stroke="#2b1d12"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            className={[isWalking && "sf-walk", isWaving && "sf-wave"].filter(Boolean).join(" ")}
          >
            <circle cx="0" cy="-20" r="4.5" />
            <line x1="0" y1="-15.5" x2="0" y2="6" />
            <line x1="0" y1="-13" x2="-5" y2="-3" />
            <g className="sf-arm-r">
              <line x1="0" y1="-13" x2="5" y2="-3" />
            </g>
            <g className="sf-leg-l">
              <line x1="0" y1="6" x2="0" y2="20" />
            </g>
            <g className="sf-leg-r">
              <line x1="0" y1="6" x2="0" y2="20" />
            </g>
          </svg>
        </div>
      </div>
    </>
  );
}
